import { inject } from '@angular/core';
import { CanActivateFn, GuardResult, Router } from '@angular/router';
import { promiseWithTimeout } from '@pinnakl/shared/util-helpers';
import { CrmWorkspacesStore, WORKSPACE_RESOLUTION_TIMEOUT_MS } from './crm-workspaces.store';

// Order matters: the workspace list can only be fetched once the user is authenticated.
//
// Fails closed. Everything behind this guard is workspace-scoped, and crmWorkspaceHeaderInterceptor
// only attaches the header once the loaded list confirms a workspace belonging to the signed-in
// user. Letting the route through after a failed load would run the whole page against the BE
// default workspace instead — silently, and possibly against another client's data.
//
// onUnresolved decides what that refusal looks like, so the "is it resolved" test — which has to
// stay in step with the interceptor — is not restated per app. The default plain false is what
// crm-app wants: AppComponent renders its retry panel in place of the router outlet. Apps with no
// such shell pass a redirect instead, so a blocked route does not leave a blank page.
// CrmWorkspacesApiService toasts the failure either way.
export const createCrmWorkspaceResolvedGuard =
  (onUnresolved: (router: Router) => GuardResult = () => false): CanActivateFn =>
  async () => {
    const workspacesStore = inject(CrmWorkspacesStore);
    const router = inject(Router);
    // A guard that never settles never settles the navigation either, and the app spinner only
    // stops on a settled one — so a stuck load has to time out into the refusal below rather than
    // leave the user on an endless loader (WI 8623).
    await promiseWithTimeout(workspacesStore.loadWorkspaces(), WORKSPACE_RESOLUTION_TIMEOUT_MS, () => {
      console.error('[workspace backstop] workspace load timed out in the route guard');
      return [];
    });
    return workspacesStore.activeWorkspaceId() !== null || onUnresolved(router);
  };

export const crmWorkspaceResolvedGuard: CanActivateFn = createCrmWorkspaceResolvedGuard();
